import Button from "components/form/Button";
import InputField from "components/form/InputField";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  downloadPdfFromApi,
  encode,
  formatCurrency,
  validateEmail,
} from "utils/HelperFunctions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { SuccessTick } from "icons/auth/SuccessTick";
import { paymentReceiptDownloadPdf } from "store/slices/paymentsSlice";
import { usePortalVanityUrl } from "store/slices/dashboardSlice";
import MessageToast from "components/ui-component/MessageToast";
import { RootState } from "store/store";

interface ProductLinkSuccessProps {
  email: string;
  amount: string | number;
  sendEmail: (val: any) => Promise<boolean>;
  invoiceSlug: string;
  orderId: string | number;
  isRetry?: boolean;
  isRecurring?: boolean;
  clusterId: string | number;
  lead_id: any;
  showSuccessToast?: boolean;
  invoiceNumber?: string;
  accountId: string;
}

const ProductLinkSuccess = ({
  email,
  amount,
  invoiceSlug,
  orderId,
  sendEmail,
  isRetry = false,
  isRecurring = false,
  clusterId,
  lead_id,
  showSuccessToast = false,
  invoiceNumber = "",
  accountId,
}: ProductLinkSuccessProps) => {
  const { companyDetails } = useAppSelector(
    (state: RootState) => state.payment
  );
  const [isErr, setIsErr] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string | undefined>(undefined);
  const [emailInput, setEmailInput] = useState("");
  const [isEmailLoading, setIsEmailLoading] = useState<boolean>(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);
  const [isRecieptDownloaded, setIsRecieptDownloaded] =
    useState<boolean>(false);
  const [msg, setMsg] = useState<any>({
    variant: "",
    content: null,
  });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const cluster_id = encode(`${clusterId}`, "ci_");
  const vanityUrl = usePortalVanityUrl();

  const handleSendEmail = async () => {
    setIsEmailLoading(true);
    if (!validateEmail(emailInput)) {
      setIsErr(true);
      setErrMsg("Invalid Email");
      setIsEmailLoading(false);
      return;
    }
    setIsEmailLoading(true);
    try {
      let value = {
        invoice_slug: invoiceSlug,
        email: emailInput,
        order_id: orderId,
        shouldSendAll: false,
        accountId,
      };
      const sent = await sendEmail(value);
      if (sent && showSuccessToast) {
        setIsEmailSent(true);
        setMsg({
          ...msg,
          variant: "success",
          content: "Receipt sent successfully",
        });
      } else if (!sent && showSuccessToast) {
        setMsg({
          ...msg,
          variant: "error",
          content: "Unable to send the receipt. Please try again.",
        });
      }
      if (sent) {
        setEmailInput("");
      }
    } catch (error) {
      console.error("Failed to send email:", error);
    } finally {
      setIsEmailLoading(false);
    }
  };

  const onPaymentDownload = async () => {
    let payload: any = {
      lead_id: lead_id,
      cluster_id: clusterId,
      url: `${vanityUrl}invoice/${invoiceSlug}`,
      isFromPortal: true,
    };

    if (invoiceSlug) {
      payload = {
        ...payload,
        invoice_slug: invoiceSlug,
      };
    } else {
      payload = {
        ...payload,
        invoice_id: orderId,
      };
    }

    const downloaded = await downloadPdfFromApi(
      () => dispatch(paymentReceiptDownloadPdf(payload)).unwrap(),
      !invoiceSlug || !invoiceNumber
        ? `Receipt - ${orderId}.pdf`
        : `Invoice - ${invoiceNumber || invoiceSlug}.pdf`,
      setIsDownloadLoading
    );

    if (downloaded && showSuccessToast) {
      setIsRecieptDownloaded(true);
      setMsg({
        ...msg,
        variant: "success",
        content:
          !invoiceSlug || !invoiceNumber
            ? "Receipt downloaded successfully"
            : "Invoice downloaded successfully",
      });
    } else if (!downloaded && showSuccessToast) {
      setMsg({
        ...msg,
        variant: "error",
        content: "Unable to download the receipt. Please try again.",
      });
    }
  };

  return (
    <div
      className={`w-full h-full relative lg:py-0 py-5 ${
        !isRetry && "max-w-[620px] px-5"
      } `}
    >
      <div className="h-full flex flex-col justify-center items-center gap-24">
        <div className="w-full flex flex-col justify-center items-center gap-10">
          <SuccessTick />
          <div className="text-txtPrimary lg:text-2xl lg:leading-8 text-xl leading-[26.04px] font-medium">
            {isRecurring
              ? "Subscription started successfully"
              : "Payment completed"}
          </div>

          <div className="w-full flex flex-col justify-center items-center">
            {isRecurring ? (
              <div className="w-full text-center bg-bgThird lg:py-5 lg:px-2 py-4 px-3 lg:text-xl text-base text-txtParagraph mb-1 font-normal">
                Your payment of{" "}
                <span className="font-bold">
                  {formatCurrency(amount) || "$0.00"}
                </span>{" "}
                has been received.
              </div>
            ) : (
              <div className="w-full flex justify-between items-end bg-bgThird lg:gap-4 gap-2 lg:py-5 lg:px-2 py-4 px-3 lg:text-xl text-base text-txtParagraph mb-1 font-bold">
                <p className="w-fit whitespace-nowrap">
                  {companyDetails?.name ||
                    companyDetails?.name ||
                    companyDetails?.company_name}
                </p>
                <div className="h-[1px] w-full bg-strokePrimary"></div>
                <span className="font-bold">
                  {formatCurrency(amount) || "$0.00"}
                </span>{" "}
              </div>
            )}
          </div>
        </div>
        <div className="w-full relative">
          <div className="flex-col flex justify-center items-center lg:gap-[30px] gap-5 w-full">
            <p className="lg:text-base text-sm text-center text-txtSecondary lg:px-5">
              A copy of this receipt will automatically be sent to{" "}
              <span className="font-bold">{email}</span>. To send a copy to an
              additional email, enter the address below.
            </p>
            <div className="px-[10px] w-full">
              <InputField
                type="email"
                name="email"
                label=""
                placeholder="Enter your email address"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  setIsErr(false);
                  setErrMsg(undefined);
                }}
                className="!py-3"
                error={errMsg}
                isErr={isErr}
              />
            </div>
            <div className="m-[10px] w-full flex justify-center items-center gap-5 px-[10px]">
              <Button
                text="Send Email"
                className="w-full"
                isLoading={isEmailLoading}
                onClick={handleSendEmail}
                isDisabled={emailInput.length === 0 && !isEmailSent}
                isSuccess={isEmailSent}
              />
              <Button
                isSuccess={isRecieptDownloaded}
                text="Download"
                className="w-full"
                isLoading={isDownloadLoading}
                onClick={() => onPaymentDownload()}
              />
            </div>
          </div>
          <div className="w-full mt-6  lg:text-base text-sm text-center text-btnPrimaryActive hover:!text-btnPrimaryHover ">
            <div
              className="cursor-pointer"
              onClick={() => navigate(`/${cluster_id}/dashboard`)}
            >
              Back to dashboard
            </div>
          </div>
        </div>
      </div>

      <div className="relative max-w-[560px] !bottom-0 z-[9999999]">
        <MessageToast
          variant={msg?.variant}
          content={msg?.content}
          isProgressBar
          width="max-w-[580px] w-full px-5"
          className=" !absolute !right-1/2 !translate-x-1/2"
          onCloseClick={() => {
            setMsg({
              ...msg,
              variant: "",
              content: null,
            });
            setIsEmailSent(false);
            setIsRecieptDownloaded(false);
          }}
        />
      </div>
    </div>
  );
};

export default ProductLinkSuccess;
